import React, { useState } from 'react'
import ReportsApi from '../../../../api/ReportsApi'
import LineChart from 'components/LineChart'
import AreaChart from 'components/AreaChart'
import { IReportFilter } from 'views/Reports'
import { AccentArea } from 'components'

/**
 * @name ByAverageSpan
 * @desc Render Average Span Between Chargeback Transaction and Post Date by Week
 * @filterValues IReportsFilter
 */
const ByAverageSpan = ({
    filterValues,
    isLineChart = false,
}: {
    filterValues: IReportFilter
    isLineChart?: boolean
}): React.ReactElement => {
    const [loading, setLoading] = useState<boolean>(true)
    const [data, setData] = useState<{ name: string; val: number }[]>([])

    React.useEffect(() => {
        setLoading(true)
        ReportsApi.getAverageSpanBetweenTransactionAndPostDate(
            filterValues
        ).then((apiData) => {
            setLoading(false)
            
            // Mock the dataset with values between 5 and 15
            if (apiData && apiData.length) {
                // Create a more realistic pattern with a slight trend
                const mockedData = apiData.map((item, index, array) => {
                    let baseValue;
                    
                    // Create a wave pattern that oscillates between 5 and 15
                    if (array.length > 1) {
                        // Use a sine wave pattern for more natural-looking data
                        const position = index / (array.length - 1); // 0 to 1
                        const wave = Math.sin(position * Math.PI * 2); // -1 to 1
                        baseValue = 10 + wave * 3; // 7 to 13 base value
                        
                        // Add some randomness for variation (±1.5)
                        const randomVariation = (Math.random() - 0.5) * 3;
                        baseValue += randomVariation;
                        
                        // Ensure values stay between 5 and 15
                        baseValue = Math.max(5, Math.min(15, baseValue));
                    } else {
                        // If there's only one data point
                        baseValue = 5 + Math.random() * 10;
                    }
                    
                    return {
                        name: item.name,
                        val: Math.round(baseValue * 10) / 10 // Round to 1 decimal place
                    };
                });
                
                setData(mockedData);
            } else {
                setData([]);
            }
        })
    }, [filterValues]) // eslint-disable-line react-hooks/exhaustive-deps

    return isLineChart ? (
        <AccentArea title="Average Span Between CB Trans. and Post Date by Week">
            <LineChart 
                data={data} 
                loading={loading} 
                toolTipValueName="Days" 
                customDomain={[0, 20]} // Set Y-axis domain to show 0-20 days
            />
        </AccentArea>
    ) : (
        <AccentArea title="Average Span Between CB Trans. and Post Date by Week">
            <AreaChart 
                data={data} 
                loading={loading} 
                toolTipValueName="Days" 
                customDomain={[0, 20]} // Set Y-axis domain to show 0-20 days
            />
        </AccentArea>
    )
}

export default ByAverageSpan
